import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestore, collection, getDocs } from './firebase-config';
import { AuthContext } from './AuthContext';
import './GoogleNotesViewer.css';

const GoogleNotesViewer = () => {
  const { loggedInUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Redirect non-admin users
  useEffect(() => {
    if (!loggedInUser || loggedInUser.username !== 'Aaron') {
      navigate('/');
    }
  }, [loggedInUser, navigate]);

  // Fetch all notes on mount
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const notesCollection = collection(firestore, 'GoogleNotesData');
        const snapshot = await getDocs(notesCollection);
        const notesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort by most recently edited
        notesList.sort((a, b) => {
          const dateA = a.editedDate?.toDate?.() || new Date(0);
          const dateB = b.editedDate?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        setNotes(notesList);
        setFilteredNotes(notesList);

        // Extract unique tags and colors for filter dropdowns
        const tags = new Set();
        const colors = new Set();
        notesList.forEach(note => {
          if (note.tags && Array.isArray(note.tags)) {
            note.tags.forEach(tag => tags.add(tag));
          }
          if (note.color && note.color !== 'DEFAULT') {
            colors.add(note.color);
          }
        });
        setAvailableTags([...tags].sort());
        setAvailableColors([...colors].sort());
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setLoading(false);
      }
    };

    if (loggedInUser?.username === 'Aaron') {
      fetchNotes();
    }
  }, [loggedInUser]);

  // Parse search query into terms (supports + for AND, | for OR, quotes for phrases)
  const parseSearchTerms = (query) => {
    if (!query.trim()) return { andGroups: [] };
    // Split by | first (OR groups), then each group by + (AND terms)
    const orGroups = query.split('|');
    const andGroups = orGroups.map(group => {
      const terms = [];
      const parts = group.split('+');
      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;
        if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
          terms.push(trimmed.slice(1, -1).toLowerCase());
        } else {
          terms.push(trimmed.toLowerCase());
        }
      }
      return terms;
    }).filter(group => group.length > 0);
    return { andGroups };
  };

  const noteMatchesSearch = (note, { andGroups }) => {
    const text = (
      (note.title || '') + ' ' +
      (note.content || '') + ' ' +
      (note.searchableText || '')
    ).toLowerCase();
    // OR between groups, AND within each group
    return andGroups.some(terms => terms.every(term => text.includes(term)));
  };

  // Filter and sort notes when criteria change
  useEffect(() => {
    let results = [...notes];

    // Full-text search with multi-term support
    if (searchQuery.trim()) {
      const parsed = parseSearchTerms(searchQuery);
      if (parsed.andGroups.length > 0) {
        results = results.filter(note => noteMatchesSearch(note, parsed));
      }
    }

    // Date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      results = results.filter(note => {
        const noteDate = note.editedDate?.toDate?.() || note.createdDate?.toDate?.();
        return noteDate && noteDate >= fromDate;
      });
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      results = results.filter(note => {
        const noteDate = note.editedDate?.toDate?.() || note.createdDate?.toDate?.();
        return noteDate && noteDate <= toDate;
      });
    }

    // Tag filter
    if (selectedTag) {
      results = results.filter(note =>
        note.tags && Array.isArray(note.tags) && note.tags.includes(selectedTag)
      );
    }

    // Color filter
    if (selectedColor) {
      results = results.filter(note => note.color === selectedColor);
    }

    // Status filter
    if (selectedStatus === 'pinned') {
      results = results.filter(note => note.isPinned);
    } else if (selectedStatus === 'archived') {
      results = results.filter(note => note.isArchived);
    } else if (selectedStatus === 'trashed') {
      results = results.filter(note => note.isTrashed);
    }

    // Sort
    results.sort((a, b) => {
      const dateA = a.editedDate?.toDate?.() || a.createdDate?.toDate?.() || new Date(0);
      const dateB = b.editedDate?.toDate?.() || b.createdDate?.toDate?.() || new Date(0);
      if (sortOrder === 'newest') return dateB - dateA;
      if (sortOrder === 'oldest') return dateA - dateB;
      // Alphabetical
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

    setFilteredNotes(results);
  }, [searchQuery, dateFrom, dateTo, sortOrder, selectedTag, selectedColor, selectedStatus, notes]);

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSortOrder('newest');
    setSelectedTag('');
    setSelectedColor('');
    setSelectedStatus('');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getColorClass = (color) => {
    if (!color || color === 'DEFAULT') return '';
    return `note-color-${color.toLowerCase()}`;
  };

  if (!loggedInUser || loggedInUser.username !== 'Aaron') {
    return null;
  }

  if (loading) {
    return <div className="google-notes-loading">Loading notes...</div>;
  }

  return (
    <div className="google-notes-viewer">
      <h1>Google Keep Notes</h1>

      {/* Filters */}
      <div className="google-notes-filters">
        <div className="filter-row">
          <input
            type="text"
            placeholder='Search: + for AND, | for OR, quotes for phrases e.g. God+Jesus | "Doug Wilson"'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="notes-search-input"
          />
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="notes-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="alphabetical">A-Z</option>
          </select>
          <button onClick={clearFilters} className="notes-clear-btn">
            Clear Filters
          </button>
        </div>
        <div className="filter-row filter-row-secondary">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="notes-date-input"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="notes-date-input"
          />
          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            className="notes-select"
          >
            <option value="">All Tags</option>
            {availableTags.length > 0 ? (
              availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))
            ) : (
              <option disabled>No tags available</option>
            )}
          </select>
          <select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="notes-select"
          >
            <option value="">All Colors</option>
            {availableColors.length > 0 ? (
              availableColors.map(color => (
                <option key={color} value={color}>{color}</option>
              ))
            ) : (
              <option disabled>No colors available</option>
            )}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="notes-select"
          >
            <option value="">All Status</option>
            <option value="pinned">Pinned</option>
            <option value="archived">Archived</option>
            <option value="trashed">Trashed</option>
          </select>
        </div>
        <div className="filter-results-count">
          Showing {filteredNotes.length} of {notes.length} notes
        </div>
      </div>

      {/* Split view */}
      <div className="google-notes-split-view">
        {/* Note list */}
        <div className="google-notes-list">
          {filteredNotes.map(note => (
            <div
              key={note.id}
              className={`google-notes-list-item ${getColorClass(note.color)} ${selectedNote?.id === note.id ? 'selected' : ''}`}
              onClick={() => setSelectedNote(note)}
            >
              <div className="note-list-title">
                {note.isPinned && <span className="note-pin-icon">📌</span>}
                {note.title || 'Untitled'}
              </div>
              <div className="note-list-preview">
                {note.content ? note.content.substring(0, 80) + (note.content.length > 80 ? '...' : '') : 'No content'}
              </div>
              <div className="note-list-meta">
                <span className="note-list-date">{formatDate(note.editedDate || note.createdDate)}</span>
                {note.isArchived && <span className="note-badge archived">Archived</span>}
                {note.isPinned && <span className="note-badge pinned">Pinned</span>}
              </div>
            </div>
          ))}
          {filteredNotes.length === 0 && (
            <div className="google-notes-empty">No notes match your filters.</div>
          )}
        </div>

        {/* Note detail */}
        <div className="google-notes-detail">
          {selectedNote ? (
            <div className={`note-detail-content ${getColorClass(selectedNote.color)}`}>
              <h2>{selectedNote.title || 'Untitled'}</h2>
              <div className="note-detail-meta">
                <span>Created: {formatDate(selectedNote.createdDate)}</span>
                <span>Edited: {formatDate(selectedNote.editedDate)}</span>
                {selectedNote.color && selectedNote.color !== 'DEFAULT' && (
                  <span>Color: {selectedNote.color}</span>
                )}
                {selectedNote.isPinned && <span className="note-badge pinned">Pinned</span>}
                {selectedNote.isArchived && <span className="note-badge archived">Archived</span>}
                {selectedNote.isTrashed && <span className="note-badge trashed">Trashed</span>}
              </div>
              {selectedNote.tags && selectedNote.tags.length > 0 && (
                <div className="note-detail-tags">
                  {selectedNote.tags.map((tag, i) => (
                    <span key={i} className="note-tag">{tag}</span>
                  ))}
                </div>
              )}
              <div className="note-detail-body">
                {selectedNote.htmlContent ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedNote.htmlContent }} />
                ) : (
                  <pre className="note-plain-text">{selectedNote.content || 'No content'}</pre>
                )}
              </div>
            </div>
          ) : (
            <div className="google-notes-no-selection">
              Select a note from the list to view its contents.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleNotesViewer;
